import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { TicketErrorCode, type validateReservation } from './utils';

type ReservationResult = ReturnType<typeof validateReservation>;

/**
 * The ticket-selection error shown on the Continue gate for the calendar
 * (step 1) and dates (step 2) steps. Returns `undefined` when `show` is false.
 *
 * When the cart is empty or the reservation minimum/maximum isn't met the
 * visitor is pointed at the exact fix; otherwise the generic "select tickets"
 * prompt. Shared so both steps stay in sync (message ids included — the
 * descriptions read "dates step" for historical reasons but apply to both).
 */
export function ticketSelectionError(
  show: boolean,
  hasTickets: boolean,
  reservation: ReservationResult
): ReactNode {
  if (!show) return undefined;

  // A selected-but-invalid party gets the specific fix rather than the generic
  // "select tickets" prompt (which reads as wrong once something is picked).
  if (hasTickets && !reservation.ok) {
    const { violation } = reservation;
    if (violation.code === TicketErrorCode.MinNotMet) {
      return (
        <FormattedMessage
          defaultMessage="Please select at least {count, plural, one {# ticket} other {# tickets}} to continue."
          description="Checkout dates step error when the selected ticket count is below the experience's minimum"
          values={{ count: violation.minReservationSeats }}
        />
      );
    }
    if (violation.code === TicketErrorCode.MaxExceeded) {
      return (
        <FormattedMessage
          defaultMessage="You can book up to {count, plural, one {# ticket} other {# tickets}} per booking."
          description="Checkout dates step error when the selected ticket count is above the experience's maximum tickets per booking"
          values={{ count: violation.maxReservationSeats }}
        />
      );
    }
    if (violation.code === TicketErrorCode.MultipleOfNotMet) {
      return (
        <FormattedMessage
          defaultMessage="Please select tickets in multiples of {count} to continue."
          description="Checkout dates step error when the selected ticket count isn't a multiple of the required step"
          values={{ count: violation.multipleOf }}
        />
      );
    }
  }

  return (
    <FormattedMessage
      defaultMessage="Please select tickets to continue."
      description="Checkout dates step error when continuing without tickets"
    />
  );
}
